
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import GenieLogo from '@/components/ui/icon/icons/GenieLogo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { Icon } from '@/components/ui/icon'
import { verifyEmailToken, resendVerificationEmail } from '@/lib/auth-client'
import { completeAuthRedirect, safeAuthRedirect } from '@/lib/auth-redirect'
import type { EmailVerificationOutcome } from '@/lib/auth-client'

/** Where a fresh verification link should land the user, matching what sign-up asks for. */
const VERIFIED_DESTINATION = '/workspace'

type ScreenState = { kind: 'loading' } | { kind: 'done'; outcome: EmailVerificationOutcome }

/** Headline and explanation for each outcome, so the page never claims more than it knows. */
const OUTCOME_COPY: Record<EmailVerificationOutcome['status'], { title: string; body: string }> = {
  verified: {
    title: 'Account verified',
    body: 'Your email address has been verified.',
  },
  'already-verified': {
    title: 'Already verified',
    body: 'This address was verified earlier. Nothing left to do.',
  },
  expired: {
    title: 'Link expired',
    body: 'Verification links are short-lived. Send yourself a new one below.',
  },
  invalid: {
    title: 'Link not valid',
    body: 'This link does not match an account. It may have been changed in transit, or already replaced by a newer one.',
  },
  failed: {
    title: 'Verification failed',
    body: 'We could not reach the server to verify your address.',
  },
}

/** The same Better Auth codes `verifyEmailToken` maps, as they arrive on a redirect. */
function outcomeForErrorCode(code: string): EmailVerificationOutcome {
  switch (code) {
    case 'TOKEN_EXPIRED':
      return { status: 'expired' }
    case 'INVALID_TOKEN':
    case 'USER_NOT_FOUND':
    case 'INVALID_USER':
      return { status: 'invalid' }
    default:
      return { status: 'failed', message: 'The verification link could not be used.' }
  }
}

export function VerifyEmail() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as {
    token?: string
    email?: string
    error?: string
    redirect?: string
  }
  const [state, setState] = useState<ScreenState>({ kind: 'loading' })
  const [email, setEmail] = useState(search.email ?? '')
  const [isResending, setIsResending] = useState(false)

  useEffect(() => {
    const token = search.token

    // Landing after Better Auth handled the link itself: when the emailed URL carries a
    // `callbackURL`, the endpoint verifies server-side and redirects here with `?error=CODE`
    // instead of answering us, so the code is all we get to go on.
    if (!token && search.error) {
      setState({ kind: 'done', outcome: outcomeForErrorCode(search.error) })
      return
    }

    // Success landing: Better Auth redirects to the callbackURL with no token and no error
    // once it has verified the address and signed the account in. The `redirect` parameter
    // is what separates that from someone opening /verify-email by hand.
    if (!token && search.redirect) {
      setState({ kind: 'done', outcome: { status: 'verified' } })
      setTimeout(() => completeAuthRedirect(search.redirect), 2000)
      return
    }

    if (!token) {
      setState({ kind: 'done', outcome: { status: 'invalid' } })
      return
    }

    let cancelled = false

    const run = async () => {
      let outcome: EmailVerificationOutcome
      try {
        outcome = await verifyEmailToken(token)
      } catch (error) {
        outcome = {
          status: 'failed',
          message: error instanceof Error ? error.message : 'We could not reach the server.',
        }
      }

      if (cancelled) return
      setState({ kind: 'done', outcome })

      // Verified means Better Auth has just signed the account in; take them straight there
      if (outcome.status === 'verified' || outcome.status === 'already-verified') {
        setTimeout(() => {
          if (!cancelled) completeAuthRedirect(search.redirect)
        }, 2000)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [search.token, search.error, search.redirect, navigate])

  const handleResend = useCallback(async () => {
    const trimmed = email.trim()
    if (!trimmed) {
      toast.error('Enter the email address you signed up with', { duration: 4000 })
      return
    }

    setIsResending(true)
    try {
      const callbackURL = new URL(VERIFIED_DESTINATION, window.location.origin).href
      const result = await resendVerificationEmail(trimmed, callbackURL)
      if (result.ok) {
        toast.success('If that address needs verifying, a new link is on its way', { duration: 5000 })
      } else {
        toast.error(result.message ?? 'We could not send a new link', { duration: 5000 })
      }
    } catch {
      toast.error('We could not send a new link. Check your connection and try again.', { duration: 5000 })
    } finally {
      setIsResending(false)
    }
  }, [email])

  if (state.kind === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted">
        <div className="text-center flex flex-col items-center max-w-md px-8">
          <GenieLogo className="mb-8 h-16 w-16" />
          <h1 className="text-2xl font-bold mb-4">Verifying your account</h1>
          <p className="text-muted-foreground mb-8">
            Please wait while we verify your email address...
          </p>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  const { outcome } = state
  const succeeded = outcome.status === 'verified' || outcome.status === 'already-verified'
  const copy = OUTCOME_COPY[outcome.status]

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="text-center flex flex-col items-center max-w-md px-8">
        <GenieLogo className="mb-8 h-16 w-16" />

        <div className="mb-6">
          <div
            className={
              succeeded
                ? 'w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center'
                : 'w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center'
            }
          >
            <Icon
              name={succeeded ? 'check' : 'x'}
              className={succeeded ? 'w-8 h-8 text-green-600 dark:text-green-400' : 'w-8 h-8 text-destructive'}
            />
          </div>
        </div>

        <h1
          className={
            succeeded
              ? 'text-2xl font-bold mb-4 text-green-600 dark:text-green-400'
              : 'text-2xl font-bold mb-4 text-destructive'
          }
        >
          {copy.title}
        </h1>
        <p className="text-muted-foreground mb-4">{copy.body}</p>

        {outcome.status === 'failed' && (
          <p className="text-sm text-muted-foreground mb-4">{outcome.message}</p>
        )}

        {succeeded ? (
          <p className="text-sm text-muted-foreground">Redirecting to your workspace...</p>
        ) : (
          <div className="w-full flex flex-col gap-3">
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              aria-label="Email address"
              autoComplete="email"
            />
            <Button size="lg" onClick={handleResend} disabled={isResending}>
              {isResending ? (
                <>
                  <Icon name="loader-2" className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send a new link'
              )}
            </Button>
            <Button variant="ghost" size="lg" onClick={() => navigate({ to: '/login' })}>
              Back to sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
